import { useEffect, useState } from 'react'
import { Card } from '../../../components/ui/Card'
import { useI18n } from '../../../utils/i18n'

function useEspWebTools() {
  useEffect(() => {
    const id = 'esp-web-tools-install-button'
    if (document.getElementById(id)) return
    const script = document.createElement('script')
    script.type = 'module'
    script.id = id
    script.src = 'https://unpkg.com/esp-web-tools@10/dist/web/install-button.js?module'
    document.head.appendChild(script)
  }, [])
}

export default function Flash() {
  const { tx } = useI18n()
  useEspWebTools()

  const [available, setAvailable] = useState([])
  const [flashkits, setFlashkits] = useState([])

  useEffect(() => {
    let ok = true
    ;(async () => {
      try {
        const res = await fetch('/flash/manifests/index.json', { cache: 'no-store' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        const items = Array.isArray(data?.items) ? data.items : []
        if (ok) setAvailable(items)
      } catch {
        if (ok) setAvailable([])
      }
    })()
    return () => {
      ok = false
    }
  }, [])

  const hasList = available.length > 0
  const hasKits = flashkits.length > 0

  useEffect(() => {
    let ok = true
    ;(async () => {
      try {
        const res = await fetch('/flash/flashkits/index.json', { cache: 'no-store' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        const items = Array.isArray(data?.items) ? data.items : []
        if (ok) setFlashkits(items)
      } catch {
        if (ok) setFlashkits([])
      }
    })()
    return () => {
      ok = false
    }
  }, [])

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-2">
        <div className="text-brand-textDark font-extrabold">{tx({ zh: '设备刷写', en: 'Device Flashing' })}</div>
        <div className="text-brand-text text-sm font-medium">
          {tx({
            zh: '两种方式：1）网页直刷（需要 Chrome/Edge + WebSerial）；2）下载离线刷写包（内网/无浏览器权限时用）。',
            en: 'Two options: (1) Flash in-browser (Chrome/Edge + WebSerial). (2) Download an offline flashing kit (for intranet/no browser permission).',
          })}
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="text-brand-textDark font-extrabold">{tx({ zh: '网页直刷（推荐）', en: 'In-Browser Flash (Recommended)' })}</div>
        <div className="text-brand-text text-sm font-medium">
          {tx({
            zh: '仅在支持 WebSerial 的浏览器可用；首次需要在系统弹窗里选择串口设备。',
            en: 'Only available in browsers that support WebSerial. The first time you will be asked to pick a serial device.',
          })}
        </div>
        {!hasList ? (
          <div className="text-sm text-brand-text font-medium">
            {tx({
              zh: '未发现可用固件（`/flash/manifests/index.json`）。先运行：`bash scripts/sync_web_flash_assets.sh ...`',
              en: 'No firmware found (`/flash/manifests/index.json`). Run: `bash scripts/sync_web_flash_assets.sh ...`',
            })}
          </div>
        ) : null}
        <div className="flex flex-col gap-3">
          {available.map((fw) => (
            <div
              key={fw.id}
              className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-2 border-brand-textDark/10 rounded-2xl p-4 bg-brand-surface"
            >
              <div className="min-w-0">
                <div className="text-brand-textDark font-extrabold truncate">{fw.label || fw.id}</div>
                <div className="text-xs text-brand-text font-bold break-all">manifest: {fw.manifest}</div>
              </div>
              <div className="flex-shrink-0">
                <esp-web-install-button manifest={fw.manifest}>{tx({ zh: '连接并刷写', en: 'Connect & Flash' })}</esp-web-install-button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 space-y-2">
        <div className="text-brand-textDark font-extrabold">{tx({ zh: '离线刷写包', en: 'Offline Flashing Kit' })}</div>
        <div className="text-brand-text text-sm font-medium">
          {tx({
            zh: '由仓库脚本生成 zip，发到内网即可使用（Windows/Mac）。需要本机已安装 Python3。',
            en: 'Generated as a zip by repo scripts and can be shared internally (Windows/Mac). Requires Python3 installed on the target machine.',
          })}
        </div>
        {!hasKits ? (
          <div className="text-sm text-brand-text font-medium">
            {tx({
              zh: '未发现可用离线包（`/flash/flashkits/index.json`）。云端可运行：`cloudctl.sh publish-firmware`',
              en: 'No offline kit found (`/flash/flashkits/index.json`). In cloud you can run: `cloudctl.sh publish-firmware`',
            })}
          </div>
        ) : null}
        <div className="flex flex-col gap-3">
          {flashkits.map((fw) => (
            <div
              key={fw.id}
              className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-2 border-brand-textDark/10 rounded-2xl p-4 bg-brand-surface"
            >
              <div className="min-w-0">
                <div className="text-brand-textDark font-extrabold truncate">{fw.label || fw.id}</div>
                <div className="text-xs text-brand-text font-bold break-all">zip: {fw.url}</div>
              </div>
              <div className="flex-shrink-0">
                <a
                  href={fw.url}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-2xl bg-brand-textDark text-white text-xs font-extrabold hover:bg-brand-text transition-colors"
                >
                  {tx({ zh: '下载 zip', en: 'Download zip' })}
                </a>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
